import type { FormikHelpers } from "formik";
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import GenericTierTwo from "../GenericTierTwo";
import GenericModule from "../GenericModule";

import {
	initialValues,
	validationSchema,
	getSchemas,
	t2InitialValues,
} from "./organicSoil";
import {
	useAppSelector,
	useThreadsProcessor,
} from "@app/hooks";
import { BEModulesClassNames, FEModules } from "@utils/moduleList";
import {
	useContextAwareGetOrganicSoilDefaults,
	useContextAwareGetOrganicSoil,
	useUpdateOrganicSoilMutation,
} from "@hooks/index";
import { selectCurrentProject } from "@features/project/projectSlice";
import useModuleHook from "../useModuleHook";
import useErrorMessage from "@utils/useErrorMessage";
import type { ModuleFull } from "./organicSoilTypes";
import { selectCurrentBuilder } from "@features/builder/builderSlice";

const OrganicSoil = () => {
	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const id = moduleList.find(({ id }) => id === FEModules.OrganicSoil)?.uniqueId;

	const {
		data: organicSoilData,
		isLoading,
		isError,
		error,
	} = useContextAwareGetOrganicSoil(id ?? 0, { refetchOnMountOrArgChange: true });
	const [
		updateOrgSoil,
		{ error: updateError, isError: isUpdateError, isLoading: isUpdateLoading },
	] = useUpdateOrganicSoilMutation();
	const { data: defaults, error: defaultsError, isError: isDefaultsError, isLoading: isLoadingDefaults } = useContextAwareGetOrganicSoilDefaults(id ?? 0, { skip: !id });
	const { errorMsg: defaultsErrorMsg } = useErrorMessage({ isError: isDefaultsError, error: defaultsError });

	const { moduleSchema, t2Schema } = useMemo(() => getSchemas(), []);
	const [initValues, setInitValues] = useState<ModuleFull>({
		module: initialValues,
		tiertwo: t2InitialValues,
	});

	const { errorMsg } = useModuleHook({
		skeletonsLoadingDeps: [isLoading, isLoadingDefaults],
		isMutateError: isUpdateError,
		mutateError: updateError,
	});
	const { errorMsg: blockingError } = useErrorMessage({ isError, error });
	const {processModuleThreads} = useThreadsProcessor<ModuleFull>();
	
	useEffect(() => {
		if (organicSoilData) {
			setInitValues({
				module: organicSoilData.module,
				tiertwo: { ...organicSoilData.tiertwo, ...defaults },
			});
			processModuleThreads({
				module: organicSoilData.module,
			})
		}
	}, [organicSoilData, defaults, processModuleThreads]);

	const handleSubmit = async (
		values: ModuleFull,
		{ resetForm }: FormikHelpers<ModuleFull>,
	) => {
		const module = { ...values.module };
		const payload = {
			data: { ...values, module },
			activityId: activeActivityId ?? 0,
		};

		try {
			values.module.id && (await updateOrgSoil(payload).unwrap());
			resetForm({ values });
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<Formik
			validationSchema={validationSchema}
			initialValues={initValues}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			<>
				<GenericModule
					title="project.organic_soil"
					t2Schema={t2Schema}
					moduleSchema={moduleSchema}
					beModuleType={BEModulesClassNames.OrganicSoil}
					isLoading={isUpdateLoading}
					footerError={errorMsg}
					blockingError={blockingError}
					isError={isUpdateError}
				/>
				{t2Schema.submodules.length > 0 ? (
					<GenericTierTwo
						title="project.organic_soil"
						t2Schema={t2Schema}
						warningMessage={defaultsErrorMsg ?? null}
					/>
				) : null}
			</>
		</Formik>
	);
};

export default OrganicSoil;
